"use client";

import * as React from "react";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { GripVertical } from "lucide-react";
import { SwipeableItem } from "@/components/ui/SwipeableItem";
import { TrashCanSVG } from "@/components/ui/SVG";
import { cn } from "../utils";
import type { SelectedProduct } from "../types";

export function ProductSortableRow({
  id,
  product,
  imageUrl,
  onRemove,
}: {
  id: string;
  product: SelectedProduct;
  imageUrl?: string | null;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const dragProps = {
    ...attributes,
    ...(listeners ?? {}),
  };

  return (
    <li ref={setNodeRef} style={style} className="list-none">
      <SwipeableItem
        className="w-full"
        group="product-card-items"
        disabled={isSorting}
        rightActions={
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
            className="mx-2 flex h-full items-center rounded-sm bg-red-600 p-2 text-xs font-semibold tracking-wide text-white uppercase"
          >
            <TrashCanSVG className="h-5 w-5" svgColor="white" />
          </button>
        }
      >
        <div
          className={cn(
            "flex items-center gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 transition",
            isDragging && "border-pinkCustom",
          )}
        >
          <button
            type="button"
            {...dragProps}
            className={cn(
              "cursor-grab rounded-md p-2 text-gray-500 transition hover:bg-gray-50 active:cursor-grabbing",
              isDragging && "text-pinkCustom",
            )}
            style={{ touchAction: "none" }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            aria-label="Drag to reorder"
          >
            <GripVertical className="h-4 w-4 rotate-90" />
          </button>
          {imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={imageUrl}
              alt={`${product.partNumber || product.productId} hero`}
              className="h-12 w-12 rounded border border-gray-100 object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-gray-200 text-[10px] text-gray-400 uppercase">
              No image
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-gray-900">
              {product.partNumber || product.productId}
            </p>
          </div>
        </div>
      </SwipeableItem>
    </li>
  );
}
